import Select from 'react-select';
import Datepicker from 'react-tailwindcss-datepicker';
import { FiDownload, FiRefreshCcw, FiSearch } from 'react-icons/fi';
import { Button } from '../ui/Button';
import { FormField, inputClassName } from '../ui/FormField';
import { StatusBadge } from '../ui/StatusBadge';
import { selectStyles } from '../ui/selectStyles';
import { isAdmin } from '../../utils/permissions';

const transactionTypeOptions = (t) => [
  { value: '', label: t('All transaction types') },
  { value: 'invoice', label: t('Invoice') },
  { value: 'payment', label: t('Payment') },
  { value: 'return', label: t('Return') },
];

const TransactionTypeFilter = ({ t, value, onChange }) => {
  const options = transactionTypeOptions(t);
  return (
    <FormField label={t('Transaction Type')} labelClassName='sr-only'>
      <Select
        styles={selectStyles}
        options={options}
        value={options.find((option) => option.value === value) || options[0]}
        onChange={(option) => onChange(option?.value || '')}
        isSearchable={false}
        placeholder={t('All transaction types')}
      />
    </FormField>
  );
};

export const TransactionsFilters = ({
  t,
  language,
  mode,
  role,
  draftFilters,
  appliedFilters,
  customerOptions,
  supplierOptions,
  filtersLoading,
  exporting,
  onLoadFilters,
  onFilterChange,
  onImmediateFilterChange,
  onApply,
  onReset,
  onAdd,
  onExport,
}) => (
  <>
    <div className='space-y-3 lg:hidden'>
      <div className='flex flex-wrap items-start gap-2'>
        <div className='min-w-[220px] flex-1'>
          <FormField label={t('Keyword')} labelClassName='sr-only'>
            <input
              className={inputClassName}
              value={draftFilters.keyword}
              placeholder={t('Filter by keyword')}
              onChange={(event) => onFilterChange('keyword', event.target.value)}
              onKeyDown={(event) => event.key === 'Enter' && onApply()}
            />
          </FormField>
        </div>
      </div>

      <div className='flex flex-wrap gap-2'>
        <div className='min-w-[180px] flex-1'>
          <TransactionTypeFilter t={t} value={draftFilters.transactionType} onChange={(value) => onImmediateFilterChange('transactionType', value)} />
        </div>
        <div className='min-w-[180px] flex-1'>
          <FormField label={t('Date')} labelClassName='sr-only'>
            <Datepicker
              name='transactionDate'
              i18n={language}
              value={draftFilters.date}
              onChange={(value) => onFilterChange('date', value)}
              asSingle
              useRange={false}
              placeholder={t('Select Date')}
              displayFormat='DD/MM/YYYY'
              inputClassName={inputClassName}
              containerClassName={(className) => `${className} transaction-date-picker`}
              popupClassName={(className) => `${className} transaction-date-popup`}
            />
          </FormField>
        </div>
        {isAdmin(role) && (
          <div className='min-w-[220px] flex-1'>
            <FormField label={t('Customer')} labelClassName='sr-only'>
              <Select
                styles={selectStyles}
                options={customerOptions}
                value={customerOptions.find((option) => option.value === draftFilters.customerId) || null}
                onChange={(option) => onImmediateFilterChange('customerId', option?.value || '')}
                onMenuOpen={onLoadFilters}
                isLoading={filtersLoading}
                isClearable
                placeholder={t('Select a customer')}
                noOptionsMessage={() => t('No customers found')}
              />
            </FormField>
          </div>
        )}
        <div className='min-w-[220px] flex-1'>
          <FormField label={t('Supplier')} labelClassName='sr-only'>
            <Select
              styles={selectStyles}
              options={supplierOptions}
              value={supplierOptions.find((option) => option.value === draftFilters.supplierId) || null}
              onChange={(option) => onImmediateFilterChange('supplierId', option?.value || '')}
              onMenuOpen={onLoadFilters}
              isLoading={filtersLoading}
              isClearable
              placeholder={t('Select a supplier')}
              noOptionsMessage={() => t('No suppliers found')}
            />
          </FormField>
        </div>
      </div>

      <div className='grid w-full grid-cols-2 gap-2 sm:w-auto'>
        <Button variant='secondary' className='w-full' onClick={onApply}><FiSearch aria-hidden='true' />{t('Search')}</Button>
        <Button variant='secondary' className='w-full' onClick={onReset}><FiRefreshCcw aria-hidden='true' />{t('Reset')}</Button>
        {mode === 'all' && isAdmin(role) && <Button variant='primary' className='w-full' onClick={onAdd} loading={filtersLoading}>{t('Add New Record')}</Button>}
        <Button variant='warning' className='w-full' onClick={onExport} loading={exporting}><FiDownload aria-hidden='true' />{t('Export to Excel')}</Button>
      </div>
    </div>

    <div className='hidden lg:flex lg:flex-wrap lg:items-end lg:gap-2'>
      <div className='w-[220px] shrink-0'>
        <FormField label={t('Keyword')} labelClassName='sr-only'>
          <input
            className={inputClassName}
            value={draftFilters.keyword}
            placeholder={t('Filter by keyword')}
            onChange={(event) => onFilterChange('keyword', event.target.value)}
            onKeyDown={(event) => event.key === 'Enter' && onApply()}
          />
        </FormField>
      </div>
      <div className='min-w-[180px]'>
        <TransactionTypeFilter t={t} value={draftFilters.transactionType} onChange={(value) => onImmediateFilterChange('transactionType', value)} />
      </div>
      <div className='min-w-[180px]'>
        <FormField label={t('Date')} labelClassName='sr-only'>
          <Datepicker
            name='transactionDate'
            i18n={language}
            value={draftFilters.date}
            onChange={(value) => onFilterChange('date', value)}
            asSingle
            useRange={false}
            placeholder={t('Select Date')}
            displayFormat='DD/MM/YYYY'
            inputClassName={inputClassName}
          />
        </FormField>
      </div>
      {isAdmin(role) && (
        <div className='min-w-[220px] flex-1'>
          <FormField label={t('Customer')} labelClassName='sr-only'>
            <Select
              styles={selectStyles}
              options={customerOptions}
              value={customerOptions.find((option) => option.value === draftFilters.customerId) || null}
              onChange={(option) => onImmediateFilterChange('customerId', option?.value || '')}
              onMenuOpen={onLoadFilters}
              isLoading={filtersLoading}
              isClearable
              placeholder={t('Select a customer')}
              noOptionsMessage={() => t('No customers found')}
            />
          </FormField>
        </div>
      )}
      <div className='min-w-[220px] flex-1'>
        <FormField label={t('Supplier')} labelClassName='sr-only'>
          <Select
            styles={selectStyles}
            options={supplierOptions}
            value={supplierOptions.find((option) => option.value === draftFilters.supplierId) || null}
            onChange={(option) => onImmediateFilterChange('supplierId', option?.value || '')}
            onMenuOpen={onLoadFilters}
            isLoading={filtersLoading}
            isClearable
            placeholder={t('Select a supplier')}
            noOptionsMessage={() => t('No suppliers found')}
          />
        </FormField>
      </div>
      <Button variant='secondary' onClick={onApply}><FiSearch aria-hidden='true' />{t('Search')}</Button>
      <Button variant='secondary' onClick={onReset}><FiRefreshCcw aria-hidden='true' />{t('Reset')}</Button>
    </div>
    <div className='mt-2 hidden justify-end gap-2 lg:flex'>
      {mode === 'all' && isAdmin(role) && <Button variant='primary' onClick={onAdd} loading={filtersLoading}>{t('Add New Record')}</Button>}
      <Button variant='warning' onClick={onExport} loading={exporting}><FiDownload aria-hidden='true' />{t('Export to Excel')}</Button>
    </div>

    <div className='mt-3 flex flex-wrap gap-2'>
      {appliedFilters.keyword && <StatusBadge tone='info'>{t('Keyword')}: {appliedFilters.keyword}</StatusBadge>}
      {appliedFilters.customerId && <StatusBadge tone='info'>{customerOptions.find((option) => option.value === appliedFilters.customerId)?.label}</StatusBadge>}
      {appliedFilters.supplierId && <StatusBadge tone='info'>{supplierOptions.find((option) => option.value === appliedFilters.supplierId)?.label}</StatusBadge>}
      {appliedFilters.transactionType && <StatusBadge tone='info'>{t(appliedFilters.transactionType === 'invoice' ? 'Invoice' : appliedFilters.transactionType === 'payment' ? 'Payment' : 'Return')}</StatusBadge>}
    </div>
  </>
);
